/**
 * The academy's courses, and which of them anybody is actually in.
 *
 * WHY THE PICKERS ARE NARROWED. This academy has over a hundred courses and learners in about a
 * dozen of them. A dropdown listing all of them makes the administrator scroll past ninety-odd
 * courses whose gradebook, progress and certificate lists are empty by definition — every one a
 * dead end. So the pickers offer the courses with people in them, and say how many they are
 * hiding rather than pretending the rest do not exist.
 *
 * ONE REQUEST, NOT TWO. This used to be one `listCourses` call per org (each pulling up to 500
 * courses, unfiltered by the server — see `studio.ts`) plus a whole academy-wide enrolment
 * fan-out purely to work out which of the courses it had just downloaded had anybody in them.
 * `academy/courses` answers both questions in the one tenant-scoped, already-paged read:
 * `has_learners` is computed server-side, so there is nothing left to derive here.
 *
 * Shared by every screen that needs a course list — same query key, so moving between
 * Enrolments, Progress, Grades and Certificates costs no extra requests.
 *
 * PRICES ARE OPT-IN, AND BOUNDED BY WHAT IS ON SCREEN (spec R6.2).
 *
 * The pricing spec asks for a `prices` map "alongside `all` / `withLearners` / `hidden`", so that
 * every admin screen agrees "by sharing a source rather than by coincidence". That argument is
 * kept; the SHAPE is not, and the reason is the size this product is being built for — 1000
 * courses per academy. Two rules follow, and both are the caller's to obey:
 *
 *   1. A screen that shows no price asks for none. `priceFor` defaults to empty and the pricing
 *      query is disabled, so Enrolments, Progress, Grades and Certificates keep costing exactly
 *      the one course request they cost today.
 *   2. A screen that shows prices passes THE ROWS IT IS RENDERING, never `all`. At 1000 courses
 *      `all` is a 1000-key request to decorate 24 visible rows, which is the same shape of
 *      mistake as the per-org fan-out this hook was written to delete — only inverted.
 *
 * The seam itself is `useAdminCoursePrices`, which batches, caps and keys the request; nothing
 * about that is this hook's business beyond handing it the right keys.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listAcademyCourses, type AcademyCourse } from '../shared/lib/academy';
import { useAdminCoursePrices } from '../shared/lib/usePricing';
import type { PricingSeam } from '../shared/lib/pricing';

export function useAcademyCourses({
  priceFor = EMPTY,
}: {
  /**
   * The course keys whose prices this screen is about to render — its visible page, not its
   * whole list. Omit it entirely on a screen that shows no price.
   */
  priceFor?: readonly string[];
} = {}) {
  const courses = useQuery({
    queryKey: ['admin-tool-courses'],
    // 200 is the endpoint's own maximum and covers every academy measured so far (the largest
    // owns ~110 courses); asking for it outright keeps this the one request the screens above
    // are built to make, rather than a first page that sometimes needs a second.
    queryFn: () => listAcademyCourses({ pageSize: 200 }),
    staleTime: 5 * 60_000,
  });

  const all: AcademyCourse[] = courses.data?.results ?? [];

  /** Courses somebody is currently enrolled in — what a picker should offer. */
  const withLearners = useMemo(() => all.filter((entry) => entry.has_learners), [all]);

  /**
   * The price document for `priceFor`, or `undefined` while it is in flight — and `undefined`
   * is not "no prices". Every consumer reads it through `coursePriceView(prices, key)`, which
   * turns a missing document into `unknown` and a loaded-but-absent row into free. That
   * distinction is the whole safety property of R6.0 and it does not survive a caller reaching
   * into the map itself, which is why one is not exposed.
   */
  const prices: PricingSeam | undefined = useAdminCoursePrices(priceFor);

  return {
    /** Every course in the academy. */
    all,
    withLearners,
    /** How many are being left out, so the UI can say so. */
    hidden: all.length - withLearners.length,
    prices,
    isPending: courses.isPending,
    isError: courses.isError,
    refetch: courses.refetch,
  };
}

/**
 * One frozen empty array, not `[]` in the default parameter.
 *
 * A fresh literal per call would be a new identity on every render, and `useAdminCoursePrices`
 * derives its cache key from the array's contents inside a `useMemo` whose dependency is that
 * array — so a new empty array each time means recomputing the key on every render of every
 * admin screen. Cheap individually; not at 1000 rows.
 */
const EMPTY: readonly string[] = Object.freeze([]);
